import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { Link as RouterLink } from 'react-router-dom';
import LegalFields from 'components/legal/LegalFields';
import { ContentPreviewAction } from 'components/shared/content/ContentPreviewDialog';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SecondaryButtonLoading from 'components/shared/buttons/SecondaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import defaultLegalFormValidations from 'common/validations/form/defaultLegal';
import routes from 'common/routes';
import requestStatus from 'common/constants/status';
import getDataToSave from 'common/form/dataToSave';
import { createLegalInitialValues } from 'common/form/initial-values/create';
import StyledButtonsContainer from 'common/theme/containers/StyledButtonsContainer';
import AppBarContext from 'context/AppBarContext';
import legalSlice from 'store/modules/legal';

const requiredFields = ['created_at', 'body', 'title'];

function LegalCreate() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['buttons', 'routes']);
  const pageTitle = t('legal.create', { ns: 'routes' });
  const formRef = useRef(null);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const status = useSelector((state) => state.legal.status);

  useEffect(() => {
    if (status === requestStatus.success && formRef.current) {
      formRef.current.restart();
    }
  }, [status]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  const onCreate = useCallback(
    (values) => {
      const dataToSave = getDataToSave(values, 'legal');
      dispatch(legalSlice.actions.create(dataToSave));
    },
    [dispatch]
  );

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        initialValues={createLegalInitialValues}
        onSubmit={onCreate}
        validate={defaultLegalFormValidations}
        render={({ handleSubmit, form, invalid, pristine, values }) => {
          formRef.current = form;

          return (
            <form onSubmit={handleSubmit}>
              <LegalFields requiredFields={requiredFields} />
              <StyledButtonsContainer>
                <ContentPreviewAction title={values.title} body={values.body} />
                <SecondaryButtonLoading
                  component={RouterLink}
                  disabled={status === requestStatus.loading}
                  style={{ marginRight: '32px' }}
                  label={t('cancel', { ns: 'buttons' })}
                  to={routes.listLegal.path}
                  type="button"
                />
                <PrimaryLoadingButton
                  disabled={invalid || pristine || status === requestStatus.loading}
                  loading={status === requestStatus.loading}
                  label={t('legal.create', { ns: 'buttons' })}
                  type="submit"
                />
              </StyledButtonsContainer>
            </form>
          );
        }}
      />
    </>
  );
}

export default LegalCreate;
